import React, { useState } from 'react';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { SecondaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import Form from '@grnhse/seedling/lib/birch/components/forms/form';
import EmailSection from './email_section';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import RejectionReasonFields from './rejection_reason_fields';
import { SelectionsHelper } from 'talent_matching/hooks/use_selections_helper';
import { useRejectionData } from 'talent_matching/hooks/use_rejection_data';
import { useUserContext } from 'talent_matching/contexts/user_context';
import { withLocalePrefix } from 'shared/utils/translation';
import {
  formatPayload,
  RejectForm,
} from 'candidate_profile_redesign/modals/reject/reject_modal';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
import { useModal } from '@grnhse/seedling/lib/birch/components/modals';
import BulkRejectConfirmationModal from 'candidate_profile_redesign/modals/reject/bulk_reject_confirmation_modal';
import ModalFooter from './modal_footer';
import { COUNT_FOR_WARNING as BULK_THRESHOLD_COUNT } from 'people/legacy/index/bulk_actions/utils';
import SkeletonLoader from '@grnhse/seedling/lib/birch/components/skeleton_loader';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import useQueryHelper from 'talent_matching/hooks/use_query_helper';

const t = withLocalePrefix('talent_matching.bulk_actions.reject');

type Props = {
  selectionsHelper: SelectionsHelper;
  closeModal: () => void;
  isOpen: boolean;
  loadingComplete: () => void;
  asSpam: boolean;
};

const RejectionModal = ({
  selectionsHelper,
  closeModal,
  isOpen,
  loadingComplete,
  asSpam,
}: Props) => {
  const {
    paths: { bulkRejectionModalPath },
    appReviewSessionId,
  } = useUserContext();

  const [submitting, setSubmitting] = useState(false);
  const [confirmationModalOpen, openConfirmationModal, closeConfirmationModal] =
    useModal();

  const {
    data: rejectionData,
    error,
    refetch,
    isFetching,
    isLoading,
  } = useRejectionData(bulkRejectionModalPath);

  const { excludePeople } = useQueryHelper();

  const loading = isFetching || isLoading;

  if (!loading) {
    loadingComplete();
  }

  if (loading || error || !rejectionData) {
    return (
      <DataEntryModal
        isOpen={isOpen}
        onDismiss={closeModal}
        ariaLabel={t('title')}
        size="large"
      >
        <DataEntryModal.Header onClick={closeModal}>
          {t('title', { count: selectionsHelper.totalSelected })}
        </DataEntryModal.Header>

        <DataEntryModal.Body>
          {loading && <SkeletonLoader columnsPerRow={1} rows={5} />}

          {!loading && (error || !rejectionData) && (
            <Stack
              spacing={Tokens.spacing['2']}
              direction="row"
              alignItems="center"
            >
              <Body>{t('error_loading')}</Body>
              <SecondaryButton onClick={() => refetch()} size="md">
                {t('retry')}
              </SecondaryButton>
            </Stack>
          )}
        </DataEntryModal.Body>
      </DataEntryModal>
    );
  }

  const spamReason = rejectionData?.rejectionReasons?.spamReason;
  const rejectionReason = asSpam ? spamReason : null;

  const defaultValues = {
    rejection_reason: rejectionReason,
    note: '',
    send_email: asSpam ? false : true,
    when: rejectionData.email.sendAt.whenEmailOptions[0],
    sendDate: null,
    template: rejectionData.email.defaultTemplate,
    from: '',
    to: '',
    cc: [],
    cc_recruiter: false,
    cc_coordinator: false,
    include_self: false,
    subject: '',
    html_body: '',
    application_ids: selectionsHelper.applicationIds,
  };

  const onInitialSubmit = async (formData: RejectForm) => {
    if (selectionsHelper.totalSelected >= BULK_THRESHOLD_COUNT) {
      openConfirmationModal();
    } else {
      onModalSubmit(formData);
    }
  };

  const onModalSubmit = async (formData: RejectForm) => {
    const payload = formatPayload(
      formData,
      rejectionData?.rejectionReasons?.spamReason?.value,
      rejectionData?.dateFormatBirchPicker,
      rejectionData?.hiringPlanId,
      rejectionData?.inProcessStageId,
      false, // TODO: update to use "bulk mode" and validation in BulkActionsController#validate
      selectionsHelper.totalSelected
    );

    const trackingPayload = {
      app_review_session_id: appReviewSessionId,
      source: 'talent_matching',
      action_source: 'bulk_rejected',
    };

    setSubmitting(true);

    try {
      await standardRequestWithErrorFlash.post(
        rejectionData?.bulkRejectCandidatesPath,
        { ...payload, ...trackingPayload },
        {
          successMessage: t('success', {
            count: selectionsHelper.totalSelected,
          }),
        }
      );
      excludePeople(payload?.application_ids ?? []);
      closeModal();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DataEntryModal
      isOpen={isOpen}
      size="large"
      onDismiss={closeModal}
      ariaLabel={t('title', { count: selectionsHelper.totalSelected })}
    >
      <Form
        onSubmit={onInitialSubmit}
        defaultValues={defaultValues}
        mode="onSubmit"
      >
        <DataEntryModal.Header onClick={closeModal}>
          {t('title', { count: selectionsHelper.totalSelected })}
        </DataEntryModal.Header>

        <DataEntryModal.Body>
          <Stack spacing={Tokens.spacing['2']}>
            <RejectionReasonFields
              rejectionData={rejectionData}
              asSpam={asSpam}
            />
            <EmailSection
              selectionsHelper={selectionsHelper}
              rejectionData={rejectionData}
              shouldDisplayEmailDomainWarning={
                rejectionData.shouldDisplayEmailDomainWarning
              }
            />
          </Stack>
        </DataEntryModal.Body>

        <ModalFooter closeModal={closeModal} disableButtons={submitting} />

        <BulkRejectConfirmationModal
          isOpen={confirmationModalOpen}
          close={closeConfirmationModal}
          applicationsToRejectInBulkCount={selectionsHelper.totalSelected}
          closeRejectModal={closeModal}
          onSubmit={onModalSubmit}
        />
      </Form>
    </DataEntryModal>
  );
};

export default RejectionModal;
